import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Callout — bloco inline persistente para instruções/avisos contextuais
 * dentro de formulários e páginas. Substitui o uso de <Alert> da shadcn em
 * telas de produto. Para feedback de ação use toast (sonner).
 */
const calloutVariants = cva(
  'relative w-full rounded-lg border p-3 text-sm flex gap-2 items-start [&>svg]:h-4 [&>svg]:w-4 [&>svg]:mt-0.5 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'border-border bg-muted/40 text-foreground [&>svg]:text-muted-foreground',
        info: 'border-info/30 bg-info/5 text-foreground [&>svg]:text-info',
        success: 'border-success/30 bg-success/5 text-foreground [&>svg]:text-success',
        warning: 'border-destructive/30 bg-destructive/5 text-foreground [&>svg]:text-destructive',
        destructive:
          'border-destructive/40 bg-destructive/5 text-destructive [&>svg]:text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

export interface CalloutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof calloutVariants> {
  icon?: React.ReactNode;
  title?: React.ReactNode;
}

export const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(
  ({ className, variant, icon, title, children, ...props }, ref) => (
    <div ref={ref} role="note" className={cn(calloutVariants({ variant }), className)} {...props}>
      {icon}
      <div className="flex-1 space-y-1">
        {title && <div className="font-medium leading-tight">{title}</div>}
        {children && <div className="text-sm [&_p]:leading-relaxed">{children}</div>}
      </div>
    </div>
  )
);
Callout.displayName = 'Callout';
